import React, { useEffect, useState } from "react";
import { X, ArrowRight, ExternalLink, CheckCircle2 } from "lucide-react";
import { submitLead, CALENDLY_URL } from "../../lib/site";
import { toast } from "sonner";

export default function LeadModal({ open, onClose }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubmitted(false);
      setSubmitting(false);
    }
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead(form);
      toast.success("Thanks — we'll be in touch shortly.");
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0A0F1E]/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="lead-modal-overlay"
    >
      <div
        className="bg-[#131929] w-full max-w-lg rounded-2xl shadow-2xl border border-white/10 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-modal"
      >
        <div className="flex items-start justify-between px-6 py-5 border-b border-white/10">
          <div>
            <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[#C9A84C] font-semibold">
              Free Strategy Call
            </div>
            <h3 className="font-display text-2xl md:text-3xl uppercase tracking-[0.03em] text-white mt-1">
              Book Your Free Demo
            </h3>
          </div>
          <button
            onClick={onClose}
            data-testid="lead-modal-close"
            className="text-white/55 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {submitted ? (
          <div className="p-6 md:p-8 text-center" data-testid="lead-modal-success">
            <CheckCircle2 className="mx-auto text-[#C9A84C]" size={42} strokeWidth={1.6} />
            <h4 className="mt-4 font-display text-2xl uppercase tracking-[0.03em] text-white">
              You're In.
            </h4>
            <p className="mt-3 text-white/70 text-sm leading-relaxed">
              Pick a time below to lock in your free 30-minute strategy call.
              We'll show you exactly how this would work for your business.
            </p>
            <a
              data-testid="lead-modal-calendly"
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary mt-6 w-full"
            >
              Open Calendly <ExternalLink size={16} />
            </a>
            <button
              onClick={onClose}
              className="mt-3 text-xs text-white/55 hover:text-white underline-offset-4 hover:underline"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="p-6 md:p-8 space-y-4" data-testid="lead-form">
            <p className="text-sm text-white/70 leading-relaxed">
              Tell us a bit about your business and we'll set you up with a
              30-minute call — no pressure, just clarity.
            </p>

            <div className="grid grid-cols-1 gap-4">
              <Field label="Full name" required>
                <input
                  data-testid="lead-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Jane Smith"
                  className="lead-input"
                  required
                />
              </Field>
              <Field label="Email" required>
                <input
                  data-testid="lead-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="jane@business.com"
                  className="lead-input"
                  required
                />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Phone">
                  <input
                    data-testid="lead-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(212) 555-0142"
                    className="lead-input"
                  />
                </Field>
                <Field label="Business">
                  <input
                    data-testid="lead-business"
                    value={form.business}
                    onChange={update("business")}
                    placeholder="Industry / company"
                    className="lead-input"
                  />
                </Field>
              </div>
              <Field label="What are you looking for?">
                <textarea
                  data-testid="lead-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us what you'd like the agent to do…"
                  className="lead-input min-h-[88px] resize-y"
                />
              </Field>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="lead-submit"
              className="btn-primary w-full disabled:opacity-60"
            >
              {submitting ? "Sending…" : (<>Submit & Pick a Time <ArrowRight size={17} /></>)}
            </button>

            <p className="text-[0.7rem] text-white/50 text-center tracking-wide">
              We respect your inbox. No spam, ever.
            </p>
          </form>
        )}
      </div>

      <style>{`
        .lead-input {
          width: 100%;
          background: #0A0F1E;
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 8px;
          padding: 10px 12px;
          font-size: 14px;
          color: #ffffff;
          outline: none;
          transition: border-color 200ms ease, box-shadow 200ms ease;
        }
        .lead-input::placeholder { color: rgba(255,255,255,0.35); }
        .lead-input:focus {
          border-color: #C9A84C;
          box-shadow: 0 0 0 3px rgba(201,168,76,0.22);
        }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[0.72rem] uppercase tracking-[0.18em] text-white/65 mb-1.5">
        {label}{required ? <span className="text-[#C9A84C]"> *</span> : null}
      </span>
      {children}
    </label>
  );
}
